import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { logError } from '../lib/errorLogger';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'innerglow_session_token';

// Configure axios to always send the token if available
const getAuthHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Set up axios interceptor to automatically add auth header
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      setUser(response.data);
    } catch {
      // Token invalid/expired — clean up
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Shared post-auth: store token, set user, detect timezone
  const postAuth = useCallback(async (data) => {
    if (data.session_token) {
      localStorage.setItem(TOKEN_KEY, data.session_token);
    }
    setUser(data);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) {
        await axios.patch(`${API}/user/timezone`, { timezone: tz }, {
          withCredentials: true,
          headers: getAuthHeaders(),
        });
      }
    } catch (err) {
      logError('AuthContext', 'Timezone detection failed', { error: err?.message });
    }
    return data;
  }, []);

  const loginWithPassword = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    return postAuth(response.data);
  }, [postAuth]);

  const register = useCallback(async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password }, { withCredentials: true });
    return postAuth(response.data);
  }, [postAuth]);

  // Custom Google OAuth if configured, otherwise Emergent-managed fallback
  const loginWithGoogle = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/google/config`);
      if (data.enabled && data.client_id) {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUri = window.location.origin + '/auth/google';
        const params = new URLSearchParams({
          client_id: data.client_id,
          redirect_uri: redirectUri,
          response_type: 'code',
          scope: 'openid email profile',
          prompt: 'select_account',
        });
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
        return;
      }
    } catch (err) {
      logError('AuthContext', 'Google config check failed', { error: err?.message });
    }
    login();
  }, [login]);

  const googleExchange = useCallback(async (code) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + '/auth/google';
    const response = await axios.post(`${API}/auth/google/exchange`, { code, redirect_uri: redirectUri }, { withCredentials: true });
    return postAuth(response.data);
  }, [postAuth]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
    } catch (err) {
      logError('AuthContext', 'Logout endpoint unreachable', { error: err?.message });
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const exchangeSession = useCallback(async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    // Store token in localStorage (works on Safari where cross-site cookies are blocked)
    if (response.data.session_token) {
      localStorage.setItem(TOKEN_KEY, response.data.session_token);
    }
    setUser(response.data);
    // Auto-detect and store user's timezone
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) {
        await axios.patch(`${API}/user/timezone`, { timezone: tz }, {
          withCredentials: true,
          headers: getAuthHeaders(),
        });
      }
    } catch (err) {
      logError('AuthContext', 'Timezone detection failed', { error: err?.message });
    }
    return response.data;
  }, []);

  const value = useMemo(() => ({
    user, setUser, loading, login, logout, exchangeSession, checkAuth,
    loginWithPassword, register, loginWithGoogle, googleExchange
  }), [user, loading, login, logout, exchangeSession, checkAuth, loginWithPassword, register, loginWithGoogle, googleExchange]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
